import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, QrCode, Hash, Plus, Smartphone, Monitor, Trash2 } from 'lucide-react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Devices = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [devices, setDevices] = useState([]);
  const [showLinkOptions, setShowLinkOptions] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [pairingCode, setPairingCode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    try {
      const response = await axios.post(
        `${API}/devices/generate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(response.data.link_token);
      setShowLinkOptions(true);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const generatePairingCode = async () => {
    try {
      const response = await axios.post(
        `${API}/devices/generate-pairing-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPairingCode(response.data.pairing_code);
      setShowLinkOptions(true);
      
      // Auto-expire after 5 minutes
      setTimeout(() => {
        setPairingCode(null);
        toast.info('Pairing code expired');
      }, 5 * 60 * 1000);
    } catch (error) {
      console.error('Failed to generate pairing code:', error);
      toast.error('Failed to generate pairing code');
    }
  };

  return (
    <div className="min-h-screen bg-background-DEFAULT flex flex-col" data-testid="devices-page">
      <div className="p-6 border-b border-border-DEFAULT">
        <div className="chat-container flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/chats')}
            className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
          >
            <ArrowLeft size={20} className="text-text-primary" />
          </button>
          <h1 className="font-heading text-2xl text-text-primary">Linked Devices</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto">
          {/* Current Device */}
          <div className="mb-8">
            <h2 className="text-sm font-medium text-text-secondary mb-3">CURRENT DEVICE</h2>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center">
                  <Monitor size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-text-primary">This Device</h3>
                  <p className="text-sm text-text-secondary">Currently active</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-status-online"></div>
              </div>
            </div>
          </div>

          {/* Link New Device Section */}
          {!showLinkOptions ? (
            <div className="mb-8">
              <h2 className="text-sm font-medium text-text-secondary mb-3">LINK NEW DEVICE</h2>
              <div className="space-y-3">
                <button
                  data-testid="generate-qr-btn"
                  onClick={generateQRCode}
                  className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                    <QrCode size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-text-primary mb-1">Show QR Code</h3>
                    <p className="text-sm text-text-secondary">Scan from another device</p>
                  </div>
                </button>

                <button
                  data-testid="generate-code-btn"
                  onClick={generatePairingCode}
                  className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                    <Hash size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-medium text-text-primary mb-1">Generate Pairing Code</h3>
                    <p className="text-sm text-text-secondary">6-digit code for linking</p>
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="bg-white rounded-3xl p-8 shadow-card border border-border-DEFAULT text-center">
                {qrCode && (
                  <div data-testid="qr-code-display">
                    <h2 className="font-heading text-xl text-text-primary mb-4">Scan QR Code</h2>
                    <p className="text-sm text-text-secondary mb-6">Open AurumChat on your new device and scan this code</p>
                    <div className="bg-white p-6 rounded-2xl inline-block border-4 border-primary">
                      <QRCode value={qrCode} size={200} />
                    </div>
                    <p className="text-xs text-text-tertiary mt-4">Code expires in 5 minutes</p>
                  </div>
                )}

                {pairingCode && (
                  <div data-testid="pairing-code-display">
                    <h2 className="font-heading text-xl text-text-primary mb-4">Pairing Code</h2>
                    <p className="text-sm text-text-secondary mb-6">Enter this code on your new device</p>
                    <div className="bg-secondary-DEFAULT rounded-2xl p-8 mb-4">
                      <div className="text-5xl font-mono font-bold text-primary tracking-widest">
                        {pairingCode}
                      </div>
                    </div>
                    <p className="text-xs text-text-tertiary">Code expires in 5 minutes</p>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowLinkOptions(false);
                    setQrCode(null);
                    setPairingCode(null);
                  }}
                  className="mt-6 h-10 px-6 rounded-full text-text-secondary hover:text-primary transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {/* Linked Devices List */}
          {devices.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-text-secondary mb-3">OTHER DEVICES</h2>
              <div className="space-y-3">
                {devices.map((device) => (
                  <div
                    key={device.id}
                    data-testid={`device-${device.id}`}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                        <Smartphone size={24} className="text-text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-text-primary">{device.device_name || 'Device'}</h3>
                        <p className="text-sm text-text-secondary">{device.device_type}</p>
                      </div>
                      <button className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-red-50 hover:text-status-error transition-colors">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Devices;